import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../../lib/supabase';
import EditableTable from './EditableTable';

/**
 * Editor for `public.agent_targets_template`.
 * Server-scoped. Server-level fallback defaults — used when no tenant rule
 * and no agency override exists.
 *
 * tier is nullable (NULL = applies to all tiers).
 */
const TargetsTemplateTab = ({ server }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!server) return;
    setLoading(true);
    setError(null);
    const { data, error: dbError } = await supabase
      .from('agent_targets_template')
      .select('*')
      .eq('server', server)
      .order('metric', { ascending: true })
      .order('tier', { ascending: true, nullsFirst: true });
    if (dbError) setError(dbError.message);
    setRows(data || []);
    setLoading(false);
  }, [server]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      key: 'metric',
      label: t('superadmin.businessRules.targets.metric', 'Metric'),
      type: 'text',
    },
    {
      key: 'tier',
      label: t('superadmin.businessRules.targets.tier', 'Tier'),
      type: 'number',
      width: '100px',
      render: (v) =>
        v == null ? (
          <span className="text-xs text-muted-foreground italic">
            {t('superadmin.businessRules.targets.allTiers', 'all')}
          </span>
        ) : (
          <span className="font-data">T{v}</span>
        ),
    },
    {
      key: 'default_value',
      label: t('superadmin.businessRules.targets.defaultValue', 'Default value'),
      type: 'decimal',
    },
  ];

  const handleSave = useCallback(
    async (row, patch) => {
      const { error: dbError } = await supabase
        .from('agent_targets_template')
        .update(patch)
        .eq('id', row.id);
      if (dbError) throw dbError;
      await load();
    },
    [load]
  );

  const handleDelete = useCallback(
    async (row) => {
      const { error: dbError } = await supabase
        .from('agent_targets_template')
        .delete()
        .eq('id', row.id);
      if (dbError) throw dbError;
      await load();
    },
    [load]
  );

  const handleAdd = useCallback(async () => {
    if (!server) return;
    const metric = window.prompt(
      t(
        'superadmin.businessRules.targets.promptMetric',
        'Metric name (e.g. recruited_creators):'
      )
    );
    if (!metric || !metric.trim()) return;
    const tierStr = window.prompt(
      t(
        'superadmin.businessRules.targets.promptTier',
        'Tier (1-10) or leave empty for "all tiers":'
      )
    );
    const tier =
      tierStr && tierStr.trim() ? Number(tierStr.trim()) : null;
    const valStr = window.prompt(
      t('superadmin.businessRules.targets.promptValue', 'Default value:')
    );
    const default_value = valStr ? Number(valStr) : 0;
    const { error: dbError } = await supabase
      .from('agent_targets_template')
      .insert({ server, metric: metric.trim(), tier, default_value });
    if (dbError) {
      window.alert(dbError.message);
      return;
    }
    await load();
  }, [server, load, t]);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {t(
          'superadmin.businessRules.targets.intro',
          'Server-level default targets used as fallback when no tenant rule or agency override exists for a metric.'
        )}
      </p>
      {error && (
        <div className="rounded-xl border border-error/30 bg-error/5 px-4 py-2 text-sm text-error">
          {error}
        </div>
      )}
      <EditableTable
        columns={columns}
        rows={rows}
        loading={loading}
        onRowSave={handleSave}
        onRowDelete={handleDelete}
        onAddRow={handleAdd}
        addRowLabel={t(
          'superadmin.businessRules.targets.add',
          'Add template row'
        )}
        emptyMessage={t(
          'superadmin.businessRules.targets.empty',
          'No template targets configured for this server yet.'
        )}
      />
    </div>
  );
};

export default TargetsTemplateTab;
